// useCookieConsentSync.ts — CORE-1404
//
// Решение по cookie для вошедшего пользователя хранится и на сервере (UserConsents):
// баннер отвечает до входа, а доказательство согласия должно быть у нас, а не
// только в localStorage браузера.

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { api } from '../services/api';
import {
  isLegacyCookieConsent,
  loadCookieConsent,
  setCookieConsent,
  subscribeCookieConsent,
} from '../services/cookieConsent';
import { COOKIE_CONSENT_LEGACY_VERSION, COOKIE_CONSENT_VERSION } from '../services/consents';
import { cookieConsentRecord, cookieSyncOnLogin } from '../services/consentLogic';
import { parseCookieChoice, sameCookieChoice, type CookieChoice } from '../services/cookieChoice';
import { acceptedDocument } from '../legal/accepted';

export function useCookieConsentSync(
  userId: string | null | undefined,
  /** `cookieConsent` из `/me`: «analytics=yes;marketing=no» или прежние accepted / declined. */
  serverValue: string | null | undefined,
): void {
  // Значение, которое мы сами только что подтянули с сервера, обратно не шлём.
  const pulled = useRef<CookieChoice | null>(null);
  const serverRef = useRef<CookieChoice | null>(null);
  serverRef.current = parseCookieChoice(serverValue);

  useEffect(() => {
    if (Platform.OS !== 'web' || !userId) return;
    let cancelled = false;

    const push = (choice: CookieChoice, source: 'banner' | 'settings') => {
      const record = cookieConsentRecord(choice, isLegacyCookieConsent(), {
        current: COOKIE_CONSENT_VERSION,
        legacy: COOKIE_CONSENT_LEGACY_VERSION,
      });
      void api
        .grantConsent({
          type: 'cookies',
          ...record,
          // CORE-1660 — редакция текста называется только для выбора по категориям:
          // ответ прежнему баннеру давался на текст, которого в реестре нет.
          ...(record.documentVersion === COOKIE_CONSENT_VERSION
            ? { documents: [acceptedDocument('cookie-notice')] }
            : {}),
          source,
        })
        .then(() => { serverRef.current = choice; })
        .catch(() => { /* повторим при следующем входе */ });
    };

    void loadCookieConsent().then((local) => {
      if (cancelled) return;
      const action = cookieSyncOnLogin(local, serverRef.current);
      if (action.kind === 'push') push(action.value, 'banner');
      if (action.kind === 'pull') {
        pulled.current = action.value;
        void setCookieConsent(action.value);
      }
    });

    const unsubscribe = subscribeCookieConsent((value) => {
      if (!value) return;
      if (pulled.current && sameCookieChoice(pulled.current, value)) {
        pulled.current = null;
        return;
      }
      if (!sameCookieChoice(value, serverRef.current)) push(value, 'settings');
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);
}
